import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../authContext';
import apiService from '../services/api.service';
import logo from '../assets/mascott-logo.png';
import '../styles/Login.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login: authLogin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const [sessionMessage, setSessionMessage] = useState(null);

  useEffect(() => {
    if (searchParams.get('expired') === 'true') {
      setSessionMessage('Your session has expired. Please log in again to continue.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      console.log('Attempting login for:', email);
      const response = await apiService.login(email, password);
      if (response.data && response.data.token) {
        localStorage.setItem('token', response.data.token);
        console.log('Token stored in localStorage:', response.data.token);
        authLogin(response.data.token);
        navigate('/dashboard');
      } else {
        console.error('Invalid response from server:', response);
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.message === 'Network Error') {
        setError('Unable to connect to the server. Please check your internet connection or try again later.');
      } else {
        setError(err.response?.data?.message || 'An error occurred during login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Attempting guest login');
      const response = await apiService.guestLogin();
      if (response.data && response.data.token) {
        localStorage.setItem('token', response.data.token);
        console.log('Guest token stored in localStorage');
        authLogin(response.data.token);
        navigate('/dashboard');
      } else {
        console.error('Invalid response from server:', response);
        setError('Invalid response from server');
      }
    } catch (err) {
      console.error('Guest login error:', err);
      if (err.message === 'Network Error') {
        setError('Unable to connect to the server. Please check your internet connection or try again later.');
      } else {
        setError(err.response?.data?.message || 'An error occurred during guest login');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="logo-container">
          <img src={logo} alt="Mascott Equipment Company" className="logo" />
        </div>
        <div className="form-container">
          <h2>Login</h2>
          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <div className="input-column">
                <div className="form-row">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="form-input"
                  />
                </div>
                <div className="form-row">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="form-input"
                  />
                </div>
              </div>
              <button type="submit" disabled={loading} className="login-button">
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </div>
          </form>
          <div className="guest-login-section">
            <p>Or view projects as a guest:</p>
            <button 
              onClick={handleGuestLogin} 
              disabled={loading} 
              className="guest-login-button"
            >
              {loading ? 'Logging in...' : 'Guest Login'}
            </button>
            <p className="guest-info">Note: Guest users can only view scheduled projects</p>
          </div>
          {sessionMessage && <p className="session-expired-message">{sessionMessage}</p>}
          {error && <p className="error-message">Error: {error}</p>}
          <p className="register-link">
            Don't have an account? <Link to="/register">Register here</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;